import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { Switch } from "@project/components/ui/switch";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { HERO_IMAGES } from "@project/lib/stock-images";
import { useI18n } from "@project/lib/use-i18n";
import {
  publicItemCategoryService,
  publicItemDonationService,
} from "@project/services/donate";
import {
  ITEM_DONATION_UNIT_OPTIONS,
  isItemDonationUnit,
  unitAmountLabel,
  unitAmountPlaceholder,
} from "@project/types/item-donation";
import { MobileNumberField } from "@project/components/mobile-number-field";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobileNumber,
  type PhoneCountryCode,
} from "@project/lib/mobile-number";

const schema = z
  .object({
    full_name: z.string().trim().min(2, "Full name is required").max(100),
    mobile_number: z.string().trim().min(1, "Mobile number is required"),
    email: z
      .string()
      .trim()
      .email("Invalid email")
      .max(255)
      .optional()
      .or(z.literal("")),
    item_category: z.string().min(1, "Please select a category"),
    item_name: z.string().trim().min(2, "Item name is required").max(200),
    quantity: z.coerce.number().min(1, "Quantity must be at least 1"),
    unit: z.string().min(1, "Please select a unit"),
    unit_qty: z.string().optional().or(z.literal("")),
    preferred_donation_date: z.string().optional().or(z.literal("")),
    needs_pickup: z.boolean(),
    pickup_address: z.string().max(500).optional().or(z.literal("")),
    area: z.string().max(100).optional().or(z.literal("")),
    landmark: z.string().max(200).optional().or(z.literal("")),
    pincode: z.string().max(10).optional().or(z.literal("")),
    message: z.string().max(1000).optional().or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    if (!isItemDonationUnit(data.unit)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a valid unit",
        path: ["unit"],
      });
      return;
    }
    const amount = Number(data.unit_qty);
    if (!data.unit_qty?.trim() || Number.isNaN(amount) || amount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Please enter ${unitAmountLabel(data.unit)} amount`,
        path: ["unit_qty"],
      });
    }
    if (!data.needs_pickup) {
      return;
    }
    if (!data.pickup_address?.trim() || data.pickup_address.trim().length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pickup address is required",
        path: ["pickup_address"],
      });
    }
    if (!data.area?.trim() || data.area.trim().length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Area is required",
        path: ["area"],
      });
    }
    if (!data.pincode?.trim() || data.pincode.trim().length < 6) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Valid pincode required",
        path: ["pincode"],
      });
    }
  });

export default function InKindForm() {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>(
    []
  );
  const [form, setForm] = useState({
    full_name: "",
    mobile_number: "",
    mobile_country: DEFAULT_PHONE_COUNTRY as PhoneCountryCode,
    email: "",
    item_category: "",
    item_name: "",
    quantity: "1",
    unit: "packet",
    unit_qty: "",
    preferred_donation_date: "",
    needs_pickup: false,
    pickup_address: "",
    area: "",
    landmark: "",
    pincode: "",
    message: "",
  });

  const set = (k: keyof typeof form, v: string | boolean) =>
    setForm(p => ({ ...p, [k]: v }));

  const setNeedsPickup = (enabled: boolean) => {
    setForm(p => ({
      ...p,
      needs_pickup: enabled,
      ...(enabled
        ? {}
        : {
            pickup_address: "",
            area: "",
            landmark: "",
            pincode: "",
          }),
    }));
  };

  useEffect(() => {
    let ignore = false;
    void publicItemCategoryService
      .listActiveByType("in-kind", { page_size: 100 })
      .then(res => {
        if (!ignore) {
          setCategories(res.data.map(c => ({ id: c.id, name: c.name })));
        }
      })
      .catch(() => {
        if (!ignore) {
          setCategories([]);
        }
      });
    return () => {
      ignore = true;
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidMobileNumber(form.mobile_number, form.mobile_country)) {
      return toast.error(
        "Enter a valid mobile number for the selected country"
      );
    }
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      return toast.error(
        parsed.error.issues[0]?.message ?? "Please check the form"
      );
    }
    setSubmitting(true);
    try {
      const needsPickup = parsed.data.needs_pickup;
      const result = await publicItemDonationService.submit({
        full_name: parsed.data.full_name,
        mobile_number: parsed.data.mobile_number,
        email: parsed.data.email ?? undefined,
        item_category: Number(parsed.data.item_category),
        item_name: parsed.data.item_name,
        quantity: parsed.data.quantity,
        unit: parsed.data.unit,
        unit_qty: Number(parsed.data.unit_qty),
        preferred_donation_date:
          parsed.data.preferred_donation_date ?? undefined,
        pickup_address: needsPickup
          ? (parsed.data.pickup_address?.trim() ?? "")
          : "",
        area: needsPickup ? (parsed.data.area?.trim() ?? "") : "",
        landmark: needsPickup
          ? parsed.data.landmark?.trim()
            ? parsed.data.landmark.trim()
            : undefined
          : undefined,
        pincode: needsPickup ? (parsed.data.pincode?.trim() ?? "") : "",
        message: parsed.data.message ?? undefined,
      });
      if (result === undefined) {
        throw new Error("Could not submit in-kind donation");
      }
      setDone(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Could not submit in-kind donation";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PageShell>
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <div
            className="bg-gold/15 anim-zoom-in mx-auto grid h-20 w-20 place-items-center rounded-full text-gold"
            style={{ animationDelay: "120ms" }}
          >
            <CheckCircle2 className="h-12 w-12" />
          </div>
          <h1
            className="anim-fade-up mt-6 font-serif text-3xl font-bold text-primary"
            style={{ animationDelay: "260ms" }}
          >
            {t("pages.inkindDonation.successTitle")}
          </h1>
          <p
            className="anim-fade-up mt-3 text-muted-foreground"
            style={{ animationDelay: "380ms" }}
          >
            {t("pages.inkindDonation.successDesc")}
          </p>
          <div
            className="anim-fade-up mt-6 flex justify-center gap-3"
            style={{ animationDelay: "500ms" }}
          >
            <Button asChild>
              <Link to="/">{t("account.visitorSuccessDialog.backToHome")}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/donate">{t("pages.inkindDonation.browseCauses")}</Link>
            </Button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHero
        eyebrow={t("pages.inkindDonation.hero.eyebrow")}
        title={t("pages.inkindDonation.hero.title")}
        subtitle={t("pages.inkindDonation.hero.subtitle")}
        image={HERO_IMAGES.inkind}
      />
      <section className="mx-auto max-w-4xl px-4 py-8 sm:py-12">
        <Button
          asChild
          variant="ghost"
          className="-ml-3 mb-6 text-muted-foreground hover:text-primary"
        >
          <Link to="/donate/items">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("pages.inkindDonation.backLink")}
          </Link>
        </Button>
        <div className="anim-fade-up" style={{ animationDelay: "600ms" }}>
          <form onSubmit={submit}>
            <div className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-sm">
              <h2 className="font-serif text-xl font-bold text-primary">
                {t("pages.inkindDonation.sections.yourDetails")}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>{t("pages.inkindDonation.labels.fullName")}</Label>
                  <Input
                    required
                    value={form.full_name}
                    onChange={e => set("full_name", e.target.value)}
                  />
                </div>
                <div>
                  <Label>{t("pages.inkindDonation.labels.mobile")}</Label>
                  <MobileNumberField
                    value={form.mobile_number}
                    country={form.mobile_country}
                    onCountryChange={code =>
                      setForm(p => ({ ...p, mobile_country: code }))
                    }
                    onChange={value => set("mobile_number", value)}
                  />
                </div>
                <div>
                  <Label>{t("pages.inkindDonation.labels.email")}</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => set("email", e.target.value)}
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h2 className="font-serif text-xl font-bold text-primary">
                  {t("pages.inkindDonation.sections.whatToDonate")}
                </h2>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div>
                    <Label data-required="true">{t("pages.inkindDonation.labels.category")}</Label>
                    <Select
                      value={form.item_category}
                      onValueChange={v => set("item_category", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("pages.inkindDonation.labels.selectCategory")} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("pages.inkindDonation.labels.quantity")}</Label>
                    <Input
                      required
                      type="number"
                      min={1}
                      value={form.quantity}
                      onChange={e => set("quantity", e.target.value)}
                      placeholder={t("pages.inkindDonation.labels.quantityPlaceholder")}
                    />
                  </div>
                  <div>
                    <Label data-required="true">{t("pages.inkindDonation.labels.unit")}</Label>
                    <Select
                      value={form.unit}
                      onValueChange={v => set("unit", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("pages.inkindDonation.labels.selectUnit")} />
                      </SelectTrigger>
                      <SelectContent>
                        {ITEM_DONATION_UNIT_OPTIONS.map(u => (
                          <SelectItem key={u.value} value={u.value}>
                            {u.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{unitAmountLabel(form.unit)}</Label>
                    <Input
                      required
                      type="number"
                      min={0.01}
                      step="any"
                      value={form.unit_qty}
                      onChange={e => set("unit_qty", e.target.value)}
                      placeholder={unitAmountPlaceholder(form.unit)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t("pages.inkindDonation.labels.itemName")}</Label>
                    <Input
                      required
                      value={form.item_name}
                      onChange={e => set("item_name", e.target.value)}
                      placeholder={t("pages.inkindDonation.labels.itemNamePlaceholder")}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{t("pages.inkindDonation.labels.preferredDate")}</Label>
                    <Input
                      type="date"
                      value={form.preferred_donation_date}
                      onChange={e =>
                        set("preferred_donation_date", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3">
                  <div>
                    <p className="text-foreground/90 text-sm font-medium">
                      {t("pages.inkindDonation.labels.needPickupToggle")}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t("pages.inkindDonation.labels.needPickupHelp")}
                    </p>
                  </div>
                  <Switch
                    checked={form.needs_pickup}
                    onCheckedChange={setNeedsPickup}
                  />
                </div>

                {form.needs_pickup ? (
                  <div className="anim-fade-up mt-4">
                    <h2 className="font-serif text-xl font-bold text-primary">
                      {t("pages.inkindDonation.sections.pickupDetails")}
                    </h2>
                    <div className="mt-3 grid gap-3">
                      <div>
                        <Label data-required="true">{t("pages.inkindDonation.labels.pickupAddress")}</Label>
                        <Textarea
                          required
                          rows={2}
                          value={form.pickup_address}
                          onChange={e => set("pickup_address", e.target.value)}
                          placeholder={t("pages.inkindDonation.labels.pickupAddressPlaceholder")}
                        />
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <Label data-required="true">{t("pages.inkindDonation.labels.area")}</Label>
                          <Input
                            required
                            value={form.area}
                            onChange={e => set("area", e.target.value)}
                            placeholder={t("pages.inkindDonation.labels.areaPlaceholder")}
                          />
                        </div>
                        <div>
                          <Label>{t("pages.inkindDonation.labels.landmark")}</Label>
                          <Input
                            value={form.landmark}
                            onChange={e => set("landmark", e.target.value)}
                            placeholder={t("pages.inkindDonation.labels.landmarkPlaceholder")}
                          />
                        </div>
                      </div>
                      <div className="md:w-1/2">
                        <Label data-required="true">{t("pages.inkindDonation.labels.pincode")}</Label>
                        <Input
                          required
                          value={form.pincode}
                          onChange={e => set("pincode", e.target.value)}
                          placeholder={t("pages.inkindDonation.labels.pincodePlaceholder")}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="border-t border-border pt-4">
                <Label>{t("pages.inkindDonation.labels.message")}</Label>
                <Textarea
                  rows={3}
                  value={form.message}
                  onChange={e => set("message", e.target.value)}
                  placeholder={t("pages.inkindDonation.labels.messagePlaceholder")}
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="hover:bg-gold/90 w-full bg-gold text-gold-foreground"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                    {t("pages.inkindDonation.labels.submitting")}
                  </>
                ) : (
                  t("pages.inkindDonation.labels.submit")
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}
